import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncData } from '@/hooks';
import { ApiError } from '@/api/client';
import { institutionExperienceApi } from '@/api/institution-experience';
import type {
  CanonicalConcept,
  ResolvedTerminology,
  TerminologyOverride,
} from '@/types/institution-experience';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import { buildTerminologyRows, fillPreview, type TerminologyRow } from './terminology-view';
import styles from '../AdminExperiencePage.module.css';

const NS = 'admin.experience.terminology';

/**
 * The vocabulary editor: the 13 canonical concepts, each showing the word actually
 * in force and WHERE it came from.
 *
 * The source badge is the heart of the screen. "Student" with no provenance looks
 * like a platform constant; "Student — from your institution type" tells the
 * administrator it is a default they are allowed to disagree with, and "Élève —
 * set by your institution" tells an auditor somebody here decided that. Editing
 * without seeing the layer is how people override a country word believing they
 * are correcting a typo.
 *
 * REVERT IS UNDOABLE, NOT CONFIRMED. Clearing an override is cheap to reverse —
 * the previous labels are in hand — so the action runs immediately and the toast
 * offers Undo, instead of interrupting with a dialog for a mistake that costs one
 * click to repair.
 */
export function TerminologyTab({ institutionId }: { institutionId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();

  // The locale being EDITED — deliberately its own state, not the UI language. A
  // French-reading administrator fixing the school's English vocabulary is a normal
  // errand, and coupling the two would make it impossible.
  const [locale, setLocale] = useState<'en' | 'fr'>(i18n.language?.startsWith('fr') ? 'fr' : 'en');

  const { state: resolvedState, reload: reloadResolved } = useAsyncData<ResolvedTerminology>(
    `experience-terminology-${institutionId}-${locale}`,
    () => institutionExperienceApi.terminology.resolve(institutionId, { locale }),
    !!institutionId,
  );

  const { state: overridesState, reload: reloadOverrides } = useAsyncData<TerminologyOverride[]>(
    `experience-terminology-overrides-${institutionId}`,
    () => institutionExperienceApi.terminology.overrides(institutionId),
    !!institutionId,
  );

  const [editing, setEditing] = useState<TerminologyRow | null>(null);

  const reloadAll = () => {
    reloadResolved();
    reloadOverrides();
  };

  const revert = async (row: TerminologyRow) => {
    const override = row.override;
    if (!override) return;
    try {
      await institutionExperienceApi.terminology.remove(institutionId, row.concept, {
        locale: override.locale,
      });
    } catch (cause) {
      // "Nothing to remove" means someone else already reverted it; the outcome the
      // admin asked for is true either way, so only a real failure is an error.
      if (!(cause instanceof ApiError && cause.status === 404)) {
        toast.error(cause instanceof Error ? cause.message : t('common.error'));
        return;
      }
    }
    reloadAll();
    toast.success(t(`${NS}.reverted`, { concept: t(`${NS}.concepts.${row.concept}`) }), {
      actionLabel: t('common.undo'),
      onAction: () => {
        institutionExperienceApi.terminology
          .put(institutionId, row.concept, {
            locale: override.locale,
            singularLabel: override.singularLabel,
            pluralLabel: override.pluralLabel,
            ...(override.shortLabel ? { shortLabel: override.shortLabel } : {}),
          })
          .then(reloadAll)
          .catch((cause: unknown) =>
            toast.error(cause instanceof Error ? cause.message : t('common.error')),
          );
      },
    });
  };

  const resolved = resolvedState.status === 'ready' ? resolvedState.data : null;
  const overrides = overridesState.status === 'ready' ? overridesState.data : [];

  const rows = useMemo(
    () => (resolved ? buildTerminologyRows(resolved, overrides, locale) : []),
    [resolved, overrides, locale],
  );

  return (
    <section className={styles.section}>
      <div className={styles.toolbar}>
        <p className={styles.toolbarLead}>{t(`${NS}.lead`)}</p>
        <div
          role="group"
          aria-label={t(`${NS}.localeLabel`)}
          className={styles.localeToggle}
        >
          {(['en', 'fr'] as const).map((code) => (
            <button
              key={code}
              type="button"
              className={locale === code ? `${styles.localeBtn} ${styles.localeBtnActive}` : styles.localeBtn}
              aria-pressed={locale === code}
              onClick={() => setLocale(code)}
            >
              {t(`${NS}.locales.${code}`)}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <AsyncPanel
          state={resolvedState}
          onRetry={reloadResolved}
          isEmpty={(data) => Object.keys(data.terms).length === 0}
          emptyTitle={t(`${NS}.empty`)}
          emptyBody={t(`${NS}.emptyBody`)}
        >
          {(data) => (
            <>
              {/* The vocabulary in a sentence, not a grid — the admin hears the words
                  in context before deciding any of them is wrong. */}
              <div className={styles.previewStrip} role="note">
                <span className={styles.previewIcon} aria-hidden="true">
                  <Icon name="announcement" size={16} />
                </span>
                <div>
                  <p className={styles.previewTitle}>{t(`${NS}.previewTitle`)}</p>
                  <p className={styles.previewSentence}>
                    {fillPreview(t(`${NS}.previewSentence`), data.terms)}
                  </p>
                  <p className={styles.previewSentence}>
                    {fillPreview(t(`${NS}.previewSentencePlural`), data.terms, 'plural')}
                  </p>
                </div>
              </div>

              {data.locale !== locale && (
                <p className={styles.localeNote} role="note">
                  {t(`${NS}.servedLocale`, { asked: locale, served: data.locale })}
                </p>
              )}

              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">{t(`${NS}.columns.concept`)}</th>
                      <th scope="col">{t(`${NS}.columns.singular`)}</th>
                      <th scope="col">{t(`${NS}.columns.plural`)}</th>
                      <th scope="col">{t(`${NS}.columns.short`)}</th>
                      <th scope="col">{t(`${NS}.columns.source`)}</th>
                      <th scope="col">
                        <span className={styles.srOnly}>{t(`${NS}.columns.actions`)}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.concept}
                        className={row.override ? styles.rowOverridden : undefined}
                      >
                        <th scope="row" className={styles.conceptCell}>
                          <span className={styles.conceptName}>
                            {t(`${NS}.concepts.${row.concept}`)}
                          </span>
                          <code className={styles.conceptCode}>{row.concept}</code>
                        </th>
                        <td>{row.term?.singular ?? <span className={styles.unresolved}>—</span>}</td>
                        <td>{row.term?.plural ?? <span className={styles.unresolved}>—</span>}</td>
                        <td>{row.term?.short ?? <span className={styles.unresolved}>—</span>}</td>
                        <td>
                          {row.source && <SourceBadge source={row.source} />}
                          {row.overriddenElsewhere && (
                            <span
                              className={styles.elsewhereHint}
                              title={t(`${NS}.overriddenElsewhereHint`)}
                            >
                              {t(`${NS}.overriddenElsewhere`)}
                            </span>
                          )}
                        </td>
                        <td className={styles.actionsCell}>
                          <button
                            type="button"
                            className={styles.ghostAction}
                            onClick={() => setEditing(row)}
                          >
                            {t('common.edit')}
                          </button>
                          {row.override && (
                            <button
                              type="button"
                              className={styles.ghostAction}
                              onClick={() => revert(row)}
                            >
                              {t(`${NS}.revert`)}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </AsyncPanel>
      </Card>

      {editing && (
        <EditTermDialog
          institutionId={institutionId}
          locale={locale}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reloadAll();
          }}
        />
      )}
    </section>
  );
}

function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  // The institution's own word is the one to notice; inherited layers stay quiet.
  const tone = source === 'INSTITUTION' || source === 'CAMPUS' ? 'info' : 'neutral';
  return <Badge tone={tone}>{t(`${NS}.sources.${source}`, { defaultValue: source })}</Badge>;
}

/**
 * The editor dialog. Seeded from the STORED override when one exists — the stored
 * row is what a save replaces — and otherwise from the resolved word, which is what
 * "start from the current value" means when the current value is inherited.
 */
function EditTermDialog({
  institutionId,
  locale,
  row,
  onClose,
  onSaved,
}: {
  institutionId: string;
  locale: string;
  row: TerminologyRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const seed = row.override
    ? {
        singular: row.override.singularLabel,
        plural: row.override.pluralLabel,
        short: row.override.shortLabel ?? '',
      }
    : {
        singular: row.term?.singular ?? '',
        plural: row.term?.plural ?? '',
        short: row.term?.short ?? '',
      };

  const [singular, setSingular] = useState(seed.singular);
  const [plural, setPlural] = useState(seed.plural);
  const [short, setShort] = useState(seed.short);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const conceptName = t(`${NS}.concepts.${row.concept}`);
  const valid = singular.trim().length > 0 && plural.trim().length > 0;

  const save = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await institutionExperienceApi.terminology.put(institutionId, row.concept, {
        locale,
        singularLabel: singular.trim(),
        pluralLabel: plural.trim(),
        ...(short.trim() ? { shortLabel: short.trim() } : {}),
      });
      toast.success(t(`${NS}.saved`, { concept: conceptName }));
      onSaved();
    } catch (cause) {
      // 403 carries "You may only administer your own institution" — the server's
      // sentence, shown as written.
      setError(cause instanceof Error ? cause.message : t('common.error'));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`${NS}.editTitle`, { concept: conceptName })}
      description={t(`${NS}.editIntro`, { locale: t(`${NS}.locales.${locale}`) })}
    >
      <div className={styles.dialogBody}>
        {/* What the save would override — the admin must know whether they are
            correcting their own earlier word or disagreeing with an inherited one. */}
        <p className={styles.dialogContext}>
          {row.override
            ? t(`${NS}.editCurrentOwn`)
            : t(`${NS}.editCurrentInherited`, {
                source: t(`${NS}.sources.${row.source ?? 'PLATFORM_DEFAULT'}`, {
                  defaultValue: row.source ?? '',
                }),
              })}
        </p>

        <FormField id={`term-singular-${row.concept}`} label={t(`${NS}.columns.singular`)} required>
          <input
            id={`term-singular-${row.concept}`}
            className={styles.textInput}
            value={singular}
            maxLength={120}
            onChange={(event) => setSingular(event.target.value)}
            disabled={submitting}
          />
        </FormField>
        <FormField id={`term-plural-${row.concept}`} label={t(`${NS}.columns.plural`)} required>
          <input
            id={`term-plural-${row.concept}`}
            className={styles.textInput}
            value={plural}
            maxLength={120}
            onChange={(event) => setPlural(event.target.value)}
            disabled={submitting}
          />
        </FormField>
        <FormField
          id={`term-short-${row.concept}`}
          label={t(`${NS}.columns.short`)}
          hint={t(`${NS}.shortHint`)}
        >
          <input
            id={`term-short-${row.concept}`}
            className={styles.textInput}
            value={short}
            maxLength={60}
            onChange={(event) => setShort(event.target.value)}
            disabled={submitting}
          />
        </FormField>

        {error && (
          <p className={styles.dialogError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} disabled={!valid} loading={submitting} loadingLabel={t('common.submitting')}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export type { CanonicalConcept };
